import React, { useState, useEffect, useContext } from 'react';
import Message from '../documentation/Message';
import Spinner from '../documentation/Spinner';
import useApi from '../api';
import { ConversationProps, MessageType, AssistantRequest, AssistantResponse, ExistingChatProps } from '../types';
import { log } from 'console';
import { useNavigate } from 'react-router-dom';

const Chat: React.FC<ExistingChatProps> = ({ docForm, docId, fetchDocument, chatId, createType, formFields, isCollapsed, setIsCollapsed }) => {
    const [loading, setLoading] = useState(false);
    const [userInput, setUserInput] = useState('');
    const [messages, setMessages] = useState<MessageType[]>([]);
    const [conversationId, setConversationId] = useState('');
    const [assistantResponse, setAssistantResponse] = useState('');
    const [chatMode, setChatMode] = useState(formFields && formFields.length > 0 ? 'search' : 'create');

    const { get, getStream, post, postStream, put, patch, remove } = useApi();

    const navigate = useNavigate();

    const chatRef = React.useRef<HTMLDivElement | null>(null);

    const messagesRef = React.useRef<MessageType[]>(messages);
    messagesRef.current = messages;

    const assistantResponseRef = React.useRef(assistantResponse);
    assistantResponseRef.current = assistantResponse;

    const scrollToBottom = () => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    };

    const consumeStream = async (stream: any) => {
        const reader = stream.getReader();
        let receivedText = ''; // To accumulate the converted text
        let conversationId = '';
        try {
            let chunkText = ''; // To store the chunk text

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                // Convert the Uint8Array to String
                const chunk = new TextDecoder().decode(value);
                chunkText += chunk;

                // Split the chunk text by newline character
                const chunks = chunkText.split('\n');

                // Process each chunk
                for (let i = 0; i < chunks.length - 1; i++) {
                    const chunk = chunks[i];

                    // Attempt to parse JSON text and handle the data
                    try {
                        const data = JSON.parse(chunk);

                        if (data.type === 'assistant' && data.content) {
                            setAssistantResponse(receivedText);
                            receivedText += data.content;
                        }

                        if (conversationId === '' && data.conversation_id) {
                            conversationId = data.conversation_id;
                        }
                    } catch (error) {
                        console.error("Error parsing JSON from chunk", error);
                    }
                }

                // Store the remaining chunk for the next iteration
                chunkText = chunks[chunks.length - 1];
            }
        } catch (error) {
            console.error("Error reading from stream", error);
        } finally {
            const newMessage = { role: "assistant", content: receivedText, created_at: new Date().toISOString() };
            setMessages(messagesRef.current.concat(newMessage));
            // Clear the assistantResponse for the next use
            setAssistantResponse('');
        }

        return conversationId;
    };

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    useEffect(() => {

        scrollToBottom();

    }, [loading]);

    const sidebarClass = isCollapsed ? 'w-4' : 'w-1/3'; // Adjust the width to your design.

    // sending message for existing document
    const sendMessageSearch = async () => {
        setLoading(true);
        const date = new Date().toISOString();
        const updatedMessages = [...messages, { role: "user", content: userInput, created_at: date }];
        setMessages(updatedMessages);

        const payload: AssistantRequest = {
            message: { role: "user", content: userInput, created_at: date },
            conversation_type: "agent",
        };

        if (conversationId !== 'new' && conversationId !== "") {
            payload['conversation_id'] = conversationId;
        }

        if (docId) {
            payload['doc_id'] = docId;
        }

        postStream('/search-assistant', payload)
            .then(async ({ stream }) => {
                const convId = await consumeStream(stream);
                setConversationId(convId);
            })
            .catch(error => console.error('Streaming error:', error));

        setLoading(false);

    };

    // sending message for new document (during creation)
    const sendMessageCreate = async () => {
        setLoading(true);
        const date = new Date().toISOString();
        const updatedMessages = [...messages, { role: "user", content: userInput, created_at: date }];
        setMessages(updatedMessages);

        const payload: AssistantRequest = {
            message: { role: "user", content: userInput, created_at: date },
            conversation_type: "agent",
        };

        if (conversationId !== 'new' && conversationId !== "") {
            payload['conversation_id'] = conversationId;
        }

        const response = await post(`/doc-gpt/`, payload);

        const assistantResponse: AssistantResponse = response.data;

        if (!assistantResponse['assistant_response'][0]?.created_at) {
            const assistantResponseItem = assistantResponse['assistant_response'][0];
            if (assistantResponseItem) {
                assistantResponseItem['created_at'] = new Date().toISOString();
            }
        }

        setMessages([...updatedMessages, ...assistantResponse['assistant_response']]);

        if (conversationId === '') {
            setConversationId(assistantResponse['conversation']['id']);
        }

        if (assistantResponse.document.id) {
            navigate(`/documents/${assistantResponse.document.id}?new=true`);
        }

        if (assistantResponse.document.doc_schema && assistantResponse.document.doc_schema.length > 0) {
            fetchDocument(assistantResponse.document.id);
        }


        setLoading(false);
    };

    const sendMessage = async () => {
        if (chatMode === 'create') {
            sendMessageCreate();
        } else {
            sendMessageSearch();
        }
    };

    // Get conversation based on document id
    useEffect(() => {
        const getConversation = async (chatId: any) => {

            try {
                setLoading(true);

                let response;

                if (chatId) {
                    response = await get(`/conversations/${chatId}`);
                } else {
                    response = await get(`/documents/${docId}/conversation`);
                }

                setMessages(response.data.messages);

                setConversationId(response.data.id);

            } catch (error) {
                console.error('Error fetching conversation:', error);
            }
            setLoading(false);
        };

        getConversation(chatId);
    }, [docId]);

    // Scroll to bottom when conversationId changes or when messages are updated
    useEffect(() => {
        scrollToBottom();
    }, [conversationId, assistantResponse]);

    return (
        <div className={`relative flex shadow-lg bg-slate-100 z-20 ${sidebarClass} transition-all duration-20`}>
            <div className={`absolute top-5 -left-3 z-60 transform cursor-pointer rounded-full border`} onClick={toggleSidebar}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="black" className={`w-6 h-6 transform ${isCollapsed ? 'rotate-180' : ''} transition-transform bg-white rounded-full shadow-md p-2`}>
                    <path fillRule="evenodd" d="M16.28 11.47a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 0 1 1.06-1.06l7.5 7.5Z" clipRule="evenodd" />
                </svg>
            </div>
            {!isCollapsed && (
                <div className="flex flex-col h-full w-full max-h-[calc(100vh-1px)]">
                    <div ref={chatRef} className="flex flex-col h-full max-h-[calc(100vh-1px)] w-full grow items-center overflow-y-auto scrollbar-thin">
                        <div className="flex justify-center mt-6">
                            <button
                                className="focus:outline-none w-32 h-8 rounded-sm flex items-center shadow-sm bg-blue-100 relative"
                                onClick={() => setChatMode(chatMode === 'create' ? 'search' : 'create')}
                            >
                                {/* Background highlight for the toggle */}
                                <div className={`absolute left-0 top-0 w-1/2 h-full bg-blue-500 rounded-sm transform duration-300 ease-in-out ${chatMode === 'search' ? 'translate-x-full' : 'translate-x-0'}`}></div>

                                {/* Text "View" */}
                                <div className="z-10 flex items-center justify-center text-slate-100 font-semibold uppercase text-sm rounded-sm pl-4 pr-4 py-1 w-1/2">
                                    Create
                                </div>

                                {/* Text "Edit" */}
                                <div className="z-10 flex items-center justify-center text-slate-100 font-semibold uppercase text-sm rounded-sm pl-4 pr-4 py-1 w-1/2">
                                    Search
                                </div>
                            </button>
                        </div>
                        <div className="p-4 pb-16 mb-4 w-full justify-center grow max-w-6xl items-start">
                            {chatMode === 'create' ? (
                                <Message key={1} role="system" content="I can help you with your document, or you can use the document builder to the left!" />
                            ) : (
                                <Message key={1} role="system" content="How can I help?" />
                            )}
                            {messages
                                .filter((message) => message.role !== "system")
                                .sort((a, b) => {
                                    const dateA = new Date(a.created_at.endsWith('Z') ? a.created_at : `${a.created_at}Z`);
                                    const dateB = new Date(b.created_at.endsWith('Z') ? b.created_at : `${b.created_at}Z`);
                                    return dateA.getTime() - dateB.getTime();
                                })
                                .map((message, index) => (
                                    <Message
                                        key={index}
                                        role={message.role}
                                        content={message.content}
                                        created_at={message.created_at}
                                    />
                                ))}
                            {assistantResponseRef.current && <Message role="assistant" content={assistantResponseRef.current} />}
                            {loading && <Spinner />}
                        </div>
                    </div>
                    <div className="sticky bottom-0 z-20 shadow-l bg-slate-50 border-t p-6">
                        <div className='mx-auto flex flex-row'>
                            <div className="relative flex w-full h-12 rounded-xl bg-white border">
                                <input
                                    className="h-full w-full border-0 text-sm text-slate-700 pr-2 focus:outline-none focus:ring-1 rounded-l-xl focus:ring-blue-200 focus:border-transparent"
                                    type="text"
                                    id="search"
                                    placeholder="Type your message..."
                                    value={userInput}
                                    onChange={(e) => setUserInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            sendMessage();
                                            setUserInput('');
                                        }
                                    }}
                                />
                                <div onClick={() => { sendMessage(); setUserInput(''); }} className="grid place-items-center h-full w-12 text-slate-300 bg-blue-200 hover:bg-blue-500 text-white rounded-r-xl">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
};

export default Chat;